import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Search,
  InfoOutlined,
} from '@material-ui/icons';
import { CircularProgress } from '@material-ui/core';
import {
  Step,
  InfoBubble,
  CalendarContainer,
  DayContainer,
  DeliveryRow,
  ControlsContainer,
  SearchContainer,
  InfoContainer,
  DateContainer,
  Line,
  LoadingProgressContainer,
  Title,
  Container,
  Description,
} from './ReviewCampaign.styled';
import { useEditor } from '../CampaignEditor';
import CalendarIcon from './CalendarIcon.png';
import { ReviewIcon, StatusIcon } from '../../SVGs/Icons';
import {
  fetchProjectedDeliveriesFromACWatcher,
  fetchProjectedDeliveriesByDate,
} from '../../api/apiACWatcher';
import ReviewCampaignDrawer from '../ReviewCampaignDrawer';
import StatusPicker from '../../StatusPicker/StatusPicker';

const ReviewCampaign = ({ confirmText, onConfirm }) => {
  const editor = useEditor();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showEmptyDays, setShowEmptyDays] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInfoExpanded, setIsInfoExpanded] = useState(() => {
    const stored = localStorage.getItem('ac_review_info_expanded');
    return stored === null ? true : stored === 'true';
  });

  const handleSetInfoExpanded = value => {
    setIsInfoExpanded(value);
    localStorage.setItem('ac_review_info_expanded', String(value));
  };

  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    selectedDate: null,
    selectedDeliveries: [],
  });

  const [allDeliveries, setAllDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Helper function to map stages to include only necessary fields
  const mapStagesForRequest = useCallback(stages => {
    return (stages || []).map(stage => ({
      _id: stage._id,
      name: stage.name,
      conditions: stage.conditions,
      template: stage.template,
    }));
  }, []);

  const goToPreviousStep = useCallback(() => {
    editor.goToStep('audience/editor');
  }, [editor]);

  // Create stable comparison keys for audience and stages
  // These will only change when the actual content changes, triggering refetch
  const editorData = editor.getData();
  const audienceKey = useMemo(
    () => JSON.stringify(editorData?.audience),
    [editorData?.audience]
  );
  const stagesKey = useMemo(
    () => JSON.stringify(mapStagesForRequest(editorData?.stages)),
    [editorData?.stages, mapStagesForRequest]
  );

  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    // Get fresh values from editor inside effect to ensure we use latest data
    const data = editor.getData();
    const audience = data?.audience;
    const stages = mapStagesForRequest(data?.stages);

    setLoading(true);
    fetchProjectedDeliveriesFromACWatcher(audience, stages, year, month)
      .then(deliveries => {
        setAllDeliveries(deliveries || []);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching deliveries:', error);
        setAllDeliveries([]);
        setLoading(false);
      });
  }, [currentMonth, audienceKey, stagesKey]);

  useEffect(() => {
    if (drawerState.selectedDate) {
      // Get fresh values from editor inside effect to ensure we use latest data
      const data = editor.getData();
      const audience = data?.audience;
      const stages = mapStagesForRequest(data?.stages);

      setDrawerLoading(true);
      fetchProjectedDeliveriesByDate(audience, stages, drawerState.selectedDate)
        .then(deliveries => {
          setDrawerState(prev => ({
            ...prev,
            selectedDeliveries: deliveries || [],
          }));
          setDrawerLoading(false);
        })
        .catch(error => {
          console.error('Error fetching deliveries by date:', error);
          setDrawerState(prev => ({
            ...prev,
            selectedDeliveries: [],
          }));
          setDrawerLoading(false);
        });
    }
  }, [drawerState.selectedDate, audienceKey, stagesKey]);

  // Handle day card click to open drawer
  const handleDayClick = useCallback((date, deliveries) => {
    if (deliveries && deliveries.length > 0) {
      setDrawerLoading(false); // Reset loading state when opening with existing data
      setDrawerState({
        isOpen: true,
        selectedDate: date,
        selectedDeliveries: deliveries,
      });
    }
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerState({
      isOpen: false,
      selectedDate: null,
      selectedDeliveries: [],
    });
    setDrawerLoading(false);
  }, []);

  // Get current status and original status
  const data = editor.getData();
  const originalStatus = data.originalStatus;

  // Helper function to normalize status - convert 'unfinished' to 'active' for display
  // StatusPicker only supports 'active', 'paused', or 'archived'
  const normalizeStatusForDisplay = status => {
    if (!status || status === 'unfinished') {
      return 'active';
    }
    return status;
  };

  // Default to original status for edited campaigns, or 'active' for new campaigns
  // Status is always 'unfinished' when editing/creating, so we show originalStatus or 'active' in the dropdown
  // We'll use a state to track the displayed status separately from the actual status
  const [displayStatus, setDisplayStatus] = useState(() => {
    // Show original status if available (edited campaign), otherwise 'active' (new campaign)
    // Normalize 'unfinished' to 'active' since StatusPicker doesn't support 'unfinished'
    return normalizeStatusForDisplay(originalStatus);
  });

  // Track if user has manually changed the status to prevent overwriting their selection
  const userHasChangedStatusRef = useRef(false);

  // Update display status only when originalStatus changes, but NOT if user has manually changed it
  // This prevents overwriting user selections when they change the dropdown
  useEffect(() => {
    // Only update if user hasn't manually changed the status
    if (!userHasChangedStatusRef.current) {
      setDisplayStatus(normalizeStatusForDisplay(originalStatus));
    }
  }, [originalStatus]);

  const handleFinish = useCallback(() => {
    const data = editor.getData();
    // Use displayStatus (which defaults to originalStatus) or the actual status
    // This ensures that if user didn't change the dropdown, we use the original status
    // If original status is unfinished, set it to active as well

    let finalStatus;
    if (userHasChangedStatusRef.current) {
      finalStatus = displayStatus;
    } else {
      finalStatus =
        data.originalStatus === 'unfinished'
          ? 'active'
          : displayStatus || data.status;
    }

    const campaignData = {
      status: finalStatus,
      name: data.name,
      description: data.description,
      audience: data.audience,
      stages: mapStagesForRequest(data.stages),
    };

    // Pass confetti trigger flag through onConfirm - confetti will trigger on campaigns page after load
    const shouldTriggerConfetti = finalStatus === 'active';

    onConfirm(campaignData, shouldTriggerConfetti);
  }, [editor, onConfirm, displayStatus]);

  // Filter deliveries by search term
  const filteredDeliveries = useMemo(() => {
    if (!searchTerm) return allDeliveries;

    const term = searchTerm.toLowerCase();
    return allDeliveries
      .map(delivery => {
        // Filter contacts within each delivery date that match the search term
        if (!delivery.contacts || !Array.isArray(delivery.contacts)) {
          return null;
        }
        const matchingContacts = delivery.contacts.filter(
          contact =>
            (contact.contactName &&
              contact.contactName.toLowerCase().includes(term)) ||
            (contact.contactEmail &&
              contact.contactEmail.toLowerCase().includes(term)) ||
            (contact.stageName &&
              contact.stageName.toLowerCase().includes(term)) ||
            (contact.subject && contact.subject.toLowerCase().includes(term))
        );
        // Only return delivery if it has matching contacts
        if (matchingContacts.length === 0) {
          return null;
        }
        // Return delivery with only matching contacts
        return {
          ...delivery,
          contacts: matchingContacts,
        };
      })
      .filter(delivery => delivery !== null);
  }, [allDeliveries, searchTerm]);

  // Group deliveries by date
  const deliveriesByDate = useMemo(() => {
    const grouped = {};
    filteredDeliveries.forEach(delivery => {
      // parse date string like "2025-11-27", to local date
      const [year, month, day] = delivery.date.split('-').map(Number);
      const deliveryDate = new Date(year, month - 1, day);

      const dateKey = deliveryDate.toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = {};
        grouped[dateKey].contacts = [];
      }
      grouped[dateKey].contacts.push(...delivery.contacts);
      grouped[dateKey].remaining = delivery.remaining;
    });
    return grouped;
  }, [filteredDeliveries]);

  // Temporary get days in current month until we have backend api endpoint ready
  const getDaysInMonth = useCallback(date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const today = new Date();

    // Reset today to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);

    const days = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);

      // Only show current day and future days
      if (currentDate >= today) {
        days.push(currentDate);
      }
    }

    return days;
  }, []);

  const daysInCurrentMonth = useMemo(() => {
    return getDaysInMonth(currentMonth);
  }, [currentMonth, getDaysInMonth]);

  const navigateMonth = useCallback(direction => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);

      // Don't allow going back before current month
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      const newMonthStart = new Date(
        newDate.getFullYear(),
        newDate.getMonth(),
        1
      );

      if (newMonthStart < currentMonthStart) {
        return prev;
      }

      return newDate;
    });
  }, []);

  const formatMonthYear = useCallback(date => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }, []);

  const isToday = useCallback(date => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  // Handle status change
  const handleStatusChange = useCallback(
    newStatus => {
      userHasChangedStatusRef.current = true; // Mark that user has manually changed the status
      setDisplayStatus(newStatus);
      editor.setField('status', newStatus);
    },
    [editor]
  );

  // Disable Finish button while projected deliveries are loading
  const isNextDisabled = loading;

  return (
    <Step
      title="Review Campaign"
      nextText={confirmText}
      onBack={goToPreviousStep}
      onNext={handleFinish}
      isNextDisabled={isNextDisabled}
    >
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ReviewIcon /> <Title>Review Campaign</Title>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <StatusIcon /> Status
          <StatusPicker value={displayStatus} onChange={handleStatusChange} />
        </div>
      </Container>
      <InfoContainer>
        <Description>
          Review projected deliveries and status for this campaign.
        </Description>
        <InfoOutlined
          onClick={() => handleSetInfoExpanded(!isInfoExpanded)}
          style={{
            width: '15px',
            marginLeft: '3px',
            marginBottom: '10px',
            cursor: 'pointer',
          }}
        />
      </InfoContainer>
      {isInfoExpanded && (
        <InfoBubble>
          <InfoBubble.Content>
            <InfoBubble.Text>
              Projected campaign deliveries are based on your stages and the
              data currently in NoteRouter. Deliveries may change as your
              contact data changes.
            </InfoBubble.Text>
            <InfoBubble.CloseButton
              onClick={() => handleSetInfoExpanded(false)}
            >
              <Close />
            </InfoBubble.CloseButton>
          </InfoBubble.Content>
        </InfoBubble>
      )}
      <ControlsContainer>
        <ControlsContainer.Left>
          <DateContainer>
            <img
              src={CalendarIcon}
              alt="Calendar Icon"
              width="20"
              height="20"
            />
            <DateContainer.MonthTitle>
              {formatMonthYear(currentMonth)}
            </DateContainer.MonthTitle>
            <DateContainer.ArrowButton
              onClick={() => navigateMonth(-1)}
              disabled={
                currentMonth.getMonth() === new Date().getMonth() &&
                currentMonth.getFullYear() === new Date().getFullYear()
              }
            >
              <ChevronLeft />
            </DateContainer.ArrowButton>
            <DateContainer.ArrowButton onClick={() => navigateMonth(1)}>
              <ChevronRight />
            </DateContainer.ArrowButton>
          </DateContainer>
        </ControlsContainer.Left>
        <ControlsContainer.Right>
          <ControlsContainer.Checkbox>
            <input
              type="checkbox"
              id="showEmptyDays"
              checked={showEmptyDays}
              onChange={e => setShowEmptyDays(e.target.checked)}
            />
            <label htmlFor="showEmptyDays">
              Show days with no projected deliveries
            </label>
          </ControlsContainer.Checkbox>

          <SearchContainer>
            <SearchContainer.Icon>
              <Search />
            </SearchContainer.Icon>
            <SearchContainer.Input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
        </ControlsContainer.Right>
      </ControlsContainer>
      <Line />
      {loading ? (
        <LoadingProgressContainer>
          <CircularProgress size={24} /> Loading projected deliveries...
        </LoadingProgressContainer>
      ) : (
        <CalendarContainer>
          <CalendarContainer.Days>
            {daysInCurrentMonth.map(day => {
              const dateKey = day.toDateString();
              const dayDeliveries = deliveriesByDate[dateKey]?.contacts || [];
              const hasDeliveries = dayDeliveries.length > 0;

              // Skip days with no deliveries if showEmptyDays is false
              if (!hasDeliveries && !showEmptyDays) {
                return null;
              }

              // Show days with no deliveries if showEmptyDays is true
              if (!hasDeliveries && showEmptyDays) {
                return (
                  <DayContainer.Empty key={`empty-${dateKey}`}>
                    <DayContainer.EmptyLeft>
                      <DayContainer.EmptyDayNumber isToday={isToday(day)}>
                        {day.getDate()}
                      </DayContainer.EmptyDayNumber>
                    </DayContainer.EmptyLeft>
                    <DayContainer.EmptyRight>
                      <DayContainer.EmptyLine />
                    </DayContainer.EmptyRight>
                  </DayContainer.Empty>
                );
              }

              return (
                <DayContainer
                  key={dateKey}
                  isToday={isToday(day)}
                  hasDeliveries={hasDeliveries}
                >
                  <DayContainer.Card isToday={isToday(day)}>
                    <DayContainer.Left isToday={isToday(day)}>
                      <DayContainer.DayNumber isToday={isToday(day)}>
                        {day.getDate()}
                      </DayContainer.DayNumber>
                    </DayContainer.Left>
                    <DayContainer.Right hasDeliveries={hasDeliveries}>
                      {dayDeliveries.map(delivery => (
                        <DeliveryRow key={delivery.id}>
                          <DeliveryRow.ContactName>
                            {delivery.contactName}
                          </DeliveryRow.ContactName>
                          <DeliveryRow.ContactEmail>
                            {delivery.contactEmail}
                          </DeliveryRow.ContactEmail>
                          <DeliveryRow.StageName>
                            {delivery.stageName}
                          </DeliveryRow.StageName>
                          <DeliveryRow.Subject>
                            {delivery.subject}
                          </DeliveryRow.Subject>
                        </DeliveryRow>
                      ))}
                      {deliveriesByDate[dateKey]?.remaining ? (
                        <DeliveryRow.ShowMore
                          onClick={() => handleDayClick(day, dayDeliveries)}
                        >
                          See {deliveriesByDate[dateKey]?.remaining} more
                        </DeliveryRow.ShowMore>
                      ) : null}
                    </DayContainer.Right>
                  </DayContainer.Card>
                </DayContainer>
              );
            })}
          </CalendarContainer.Days>
        </CalendarContainer>
      )}
      <ReviewCampaignDrawer
        isOpen={drawerState.isOpen}
        onClose={handleCloseDrawer}
        deliveries={drawerState.selectedDeliveries}
        date={drawerState.selectedDate}
        campaignName={data.name}
        loading={drawerLoading}
      />
    </Step>
  );
};

export default ReviewCampaign;
